import React from "react";
import PropTypes from "prop-types";
import Skeleton from "./Skeleton";

const stack = { display: "flex", flexDirection: "column", gap: 10 };

function InvoicePreviewSkeleton({ label }) {
  return (
    <div
      className="customer-invoice-preview-page"
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      <span className="skeleton-sr-only">{label}</span>
      <div className="customer-invoice-preview-topbar">
        <Skeleton width={120} height={40} radius={8} />
        <div className="customer-invoice-preview-actions">
          <Skeleton width={142} height={40} radius={8} />
          <Skeleton width={126} height={40} radius={8} />
        </div>
      </div>

      <header className="customer-invoice-preview-header">
        <Skeleton width={290} height={32} radius={6} />
      </header>

      <section className="customer-invoice-preview-feedback">
        <Skeleton width={138} height={14} radius={4} />
        <Skeleton width={230} height={13} radius={4} />
      </section>

      <article className="customer-invoice-preview-card">
        <div className="preview-issuer-identity">
          <Skeleton width={210} height={27} radius={6} />
          <div className="preview-doc-identity">
            <Skeleton width={74} height={14} radius={4} />
            <Skeleton width={126} height={22} radius={5} />
            <Skeleton width={72} height={24} radius={999} />
          </div>
        </div>

        <hr className="preview-divider" />

        <div className="preview-meta-grid">
          {Array.from({ length: 6 }, (_, index) => (
            <div className="preview-meta-cell" key={index}>
              <Skeleton width="42%" height={11} radius={4} />
              <Skeleton width={index === 0 ? "82%" : "68%"} height={15} radius={4} />
              {index === 0 && <Skeleton width="72%" height={11} radius={4} />}
            </div>
          ))}
        </div>

        <div className="preview-items-section">
          <div className="preview-items-header">
            <Skeleton width={142} height={20} radius={5} />
            <Skeleton width={330} height={12} radius={4} />
          </div>
          <div className="preview-table-container">
            <table className="preview-items-table">
              <thead>
                <tr>
                  {["34%", "8%", "14%", "9%", "17%", "18%"].map(
                    (width, index) => (
                      <th key={index} style={{ width }}>
                        <Skeleton width="72%" height={11} radius={4} />
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {[0, 1].map((row) => (
                  <tr key={row}>
                    {Array.from({ length: 6 }, (_, column) => (
                      <td key={column}>
                        <Skeleton
                          width={column === 0 ? (row ? "64%" : "82%") : "70%"}
                          height={13}
                          radius={4}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="preview-bottom-section">
          <div className="preview-notes-column">
            {[0, 1, 2, 3].map((group) => (
              <div className="preview-note-group" key={group}>
                <Skeleton width={group < 2 ? "36%" : "28%"} height={13} radius={4} />
                <Skeleton width="96%" height={12} radius={4} />
                <Skeleton width={group % 2 ? "68%" : "84%"} height={12} radius={4} />
              </div>
            ))}
          </div>
          <div className="preview-totals-column">
            {[0, 1, 2].map((row) => (
              <div className="preview-totals-row" key={row}>
                <Skeleton width="58%" height={12} radius={4} />
                <Skeleton width="28%" height={14} radius={4} />
              </div>
            ))}
            <hr className="totals-divider" />
            <div className="preview-totals-row grand-total">
              <Skeleton width="52%" height={15} radius={4} />
              <Skeleton width="40%" height={24} radius={5} />
            </div>
            <Skeleton width="92%" height={12} radius={4} />
            <Skeleton width="76%" height={12} radius={4} />
            <hr className="totals-divider" />
            <div className="preview-amount-due-box">
              <Skeleton width="36%" height={16} radius={4} />
              <Skeleton width="46%" height={24} radius={5} />
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

InvoicePreviewSkeleton.propTypes = {
  label: PropTypes.string.isRequired,
};

export default function DocumentPreviewSkeleton({ label, variant }) {
  if (variant === "invoice") return <InvoicePreviewSkeleton label={label} />;

  return (
    <div
      className="document-preview-skeleton"
      role="status"
      aria-busy="true"
      aria-label={label}
      style={{ ...stack, gap: 18, width: "100%" }}
    >
      <span className="skeleton-sr-only">{label}</span>

      <div
        className="document-preview-skeleton__header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 24,
        }}
      >
        <div style={stack}>
          <Skeleton width={230} height={30} radius={6} />
          <div style={{ display: "flex", gap: 8 }}>
            <Skeleton width={92} height={20} radius={999} />
            <Skeleton width={72} height={20} radius={999} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Skeleton width={128} height={40} radius={8} />
          <Skeleton width={118} height={40} radius={8} />
        </div>
      </div>

      <div
        className="document-preview-skeleton__body"
        style={{
          display: "grid",
          gap: 20,
        }}
      >
        <div style={{ ...stack, gap: 16 }}>
          <div
            className="document-preview-skeleton__metadata"
            style={{
              display: "grid",
              gap: 18,
              padding: 20,
              border: "1px solid var(--border-subtle, #d8e0df)",
              borderRadius: 14,
            }}
          >
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} style={stack}>
                <Skeleton width="42%" height={11} radius={4} />
                <Skeleton width="76%" height={15} radius={4} />
              </div>
            ))}
          </div>

          <div
            style={{
              padding: 20,
              border: "1px solid var(--border-subtle, #d8e0df)",
              borderRadius: 14,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2.2fr 0.55fr 0.8fr 0.55fr 0.9fr",
                gap: 14,
                marginBottom: 16,
              }}
            >
              {["62%", "48%", "58%", "45%", "55%"].map((width, index) => (
                <Skeleton key={index} width={width} height={12} radius={4} />
              ))}
            </div>
            {[0, 1, 2].map((row) => (
              <div
                key={row}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2.2fr 0.55fr 0.8fr 0.55fr 0.9fr",
                  gap: 14,
                  padding: "13px 0",
                  borderTop: "1px solid var(--border-subtle, #e4e9e8)",
                }}
              >
                <Skeleton width={row === 1 ? "72%" : "84%"} height={14} />
                <Skeleton width="54%" height={14} />
                <Skeleton width="75%" height={14} />
                <Skeleton width="55%" height={14} />
                <Skeleton width="78%" height={14} />
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 20,
              padding: 20,
              border: "1px solid var(--border-subtle, #d8e0df)",
              borderRadius: 14,
            }}
          >
            {[0, 1].map((column) => (
              <div key={column} style={stack}>
                <Skeleton width="44%" height={14} radius={4} />
                <Skeleton width="100%" height={12} radius={4} />
                <Skeleton width="92%" height={12} radius={4} />
                <Skeleton width="68%" height={12} radius={4} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...stack, gap: 16 }}>
          <div
            style={{
              ...stack,
              padding: 20,
              border: "1px solid var(--border-subtle, #d8e0df)",
              borderRadius: 14,
            }}
          >
            <Skeleton width="52%" height={18} radius={5} />
            {[0, 1, 2, 3].map((row) => (
              <div
                key={row}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Skeleton width="54%" height={12} />
                <Skeleton width="28%" height={12} />
              </div>
            ))}
            <Skeleton width="100%" height={1} animation={false} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Skeleton width="42%" height={17} />
              <Skeleton width="38%" height={24} />
            </div>
          </div>
          <div
            style={{
              ...stack,
              padding: 20,
              border: "1px solid var(--border-subtle, #d8e0df)",
              borderRadius: 14,
            }}
          >
            <Skeleton width="58%" height={16} />
            <Skeleton width="100%" height={12} />
            <Skeleton width="86%" height={12} />
            <Skeleton width="64%" height={12} />
          </div>
        </div>
      </div>
    </div>
  );
}

DocumentPreviewSkeleton.defaultProps = {
  label: "Loading document preview",
  variant: "document",
};

DocumentPreviewSkeleton.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.oneOf(["document", "invoice"]),
};
